/**
 * useLocaleSync Hook
 *
 * Syncs user's preferred locale between:
 * - Browser cookie (persistence for guests across sessions)
 * - SFCC customer profile (for logged-in users)
 *
 * Uses SCAPI Shopper Customers API for updating customer attributes.
 * Uses SFCC system attribute `preferredLocale` (not a custom c_ attribute).
 *
 * Flow:
 * 1. On login: Load preferredLocale from account → save to cookie (if account has preference)
 * 2. On locale change: Save to cookie, and if logged in → save to account via SCAPI
 * 3. On app init: Read cookie preference for getUserPreferredLocales()
 */

import {useCallback, useEffect, useState} from 'react';

import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';

import {
  getPreferredLocaleFromCookie,
  setPreferredLocaleToCookie,
  LOCALE_CHANGED_EVENT,
} from '@amblique/king-living/app/utils/utils';

/**
 * Hook to sync locale preference between cookie and SFCC account
 *
 * @returns {Object} { syncLocaleToAccount, setPreferredLocale, preferredLocale, isLoading, error }
 */
export const useLocaleSync = () => {
  const {site} = useMultiSite();
  const siteId = site?.id;

  const {data: customer, isLoading: isCustomerLoading} = useCurrentCustomer();
  const {isRegistered, customerId} = customer || {};

  // Use SCAPI mutation for updating customer
  const updateCustomerMutation = useShopperCustomersMutation('updateCustomer');

  const [error, setError] = useState(null);
  const [preferredLocale, setPreferredLocaleState] = useState(() => {
    // Initialize from cookie on mount
    return getPreferredLocaleFromCookie(siteId);
  });

  // Sync from account to cookie when user logs in
  useEffect(() => {
    if (!siteId || isCustomerLoading) return;

    // Only sync if user is registered and has a preferred locale in their account
    if (isRegistered && customer?.preferredLocale) {
      try {
        const accountLocale = customer.preferredLocale;

        // Validate it's a supported locale for this site
        const supportedLocales = site?.l10n?.supportedLocales?.map((l) => l.id) || [];

        if (supportedLocales.includes(accountLocale)) {
          const cookieLocale = getPreferredLocaleFromCookie(siteId);

          // Only update cookie if account has different data
          if (cookieLocale !== accountLocale) {
            setPreferredLocaleToCookie(siteId, accountLocale);
            setPreferredLocaleState(accountLocale);
          }
        }
      } catch {
        // Invalid locale in account, ignore
      }
    }
  }, [siteId, isRegistered, customer?.preferredLocale, isCustomerLoading, site?.l10n?.supportedLocales]);

  // Update local state when cookie changes
  useEffect(() => {
    if (!siteId) return;

    const locale = getPreferredLocaleFromCookie(siteId);

    setPreferredLocaleState(locale);
  }, [siteId]);

  // Function to sync locale to account using SCAPI
  const syncLocaleToAccount = useCallback(
    async (localeId) => {
      if (!isRegistered || !customerId || !localeId) {
        return false;
      }

      setError(null);

      try {
        // Use SCAPI to update customer with system attribute
        await updateCustomerMutation.mutateAsync({
          parameters: {customerId},
          body: {
            // preferredLocale is a system attribute (no c_ prefix)
            preferredLocale: localeId,
          },
        });

        return true;
      } catch (err) {
        setError(err);

        return false;
      }
    },
    [isRegistered, customerId, updateCustomerMutation]
  );

  /**
   * Set preferred locale - saves to cookie and syncs to account if logged in
   * @param {string} localeId - The locale to save (e.g., 'en-CA', 'fr-CA')
   */
  const setPreferredLocale = useCallback(
    async (localeId) => {
      if (!siteId || !localeId) return;

      // Always save to cookie (for guests and registered users)
      setPreferredLocaleToCookie(siteId, localeId);
      setPreferredLocaleState(localeId);

      // If registered, also sync to account
      if (isRegistered && customerId) {
        await syncLocaleToAccount(localeId);
      }
    },
    [siteId, isRegistered, customerId, syncLocaleToAccount]
  );

  // Listen for locale changes from other components
  useEffect(() => {
    if (!siteId) return;

    const handleLocaleChanged = async (event) => {
      const {localeId} = event.detail || {};

      if (localeId) {
        setPreferredLocaleState(localeId);

        // Sync to account if registered
        if (isRegistered && customerId) {
          await syncLocaleToAccount(localeId);
        }
      }
    };

    window.addEventListener(LOCALE_CHANGED_EVENT, handleLocaleChanged);

    return () => {
      window.removeEventListener(LOCALE_CHANGED_EVENT, handleLocaleChanged);
    };
  }, [siteId, isRegistered, customerId, syncLocaleToAccount]);

  return {
    syncLocaleToAccount,
    setPreferredLocale,
    preferredLocale,
    isLoading: updateCustomerMutation.isPending,
    error,
    isRegistered,
  };
};

export default useLocaleSync;
